import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../../../PageLayout';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../../../store/GeneralStore';
import { RunTotalAudit, fetchRunTotalAudits, getRunTotalAuditsSubject } from '../../../../../store/RunTotalAudit';
import { DefaultRunTotalAuditGridColumnDefs } from './RunTotalAuditGridColumnDefs';
import { getCustomizationSubject, PrefKey, UserCustomizations } from '../../../../../store/UserCustomizations';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { buildAdvancedFilterSet, getColsForTab, getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewRunTotalAuditDialog } from '../../../../Compounds/NewRunTotalAuditDialog/NewRunTotalAuditDialog';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';

export const RunTotalAuditTab = () => {
  const [runTotalAuditsLoading, setRunTotalAuditsLoading] = useState<boolean>(false);
  const [runTotalAudits, setRunTotalAudits] = useState<PaginatedResponse<RunTotalAudit>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const [isRunTotalAuditFormOpen, setIsRunTotalAuditFormOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const filterKeys = DefaultRunTotalAuditGridColumnDefs.filter((column) => column.filterable).map(
    (column) => column.field
  );

  const handleFetchRunTotalAudits = async (
    filters?: ApiAdvancedFilter,
    localLoading?: boolean,
    forceRefresh = false
  ) => {
    if (localLoading) setRunTotalAuditsLoading(true);

    await fetchRunTotalAudits(
      {
        ...filters
      },
      forceRefresh
    );
    if (localLoading) setRunTotalAuditsLoading(false);
  };

  const getInitialSort = (userCust: UserCustomizations) => {
    const currentPage = 'runTotalAuditGrid' as PrefKey;
    const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
    return savedSort?.length ? savedSort : ([] as GridSortModel);
  };

  const getInitialFilter = (userCust: UserCustomizations) => {
    const currentPage = 'runTotalAuditGrid' as PrefKey;
    const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
    return savedFilter || ({} as GridFilterModel);
  };

  const dataFetch = useCallback(() => {
    if (userCustomizations) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchRunTotalAudits(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [userCustomizations, getInitialSort, getInitialFilter, handleFetchRunTotalAudits, filterKeys]);

  useEffect(() => {
    const auditSub = getRunTotalAuditsSubject().subscribe((a) => setRunTotalAudits(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));

    return () => {
      if (auditSub) auditSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (userCustomizations) dataFetch();
  }, [userCustomizations]);

  const tabKey = 'runTotalAuditGrid' as PrefKey;
  const colsForTab = getColsForTab(DefaultRunTotalAuditGridColumnDefs, []);
  const gridSettings = userCustomizations?.content?.[tabKey];
  let initialState;
  if (gridSettings) {
    initialState = getInitialState(gridSettings, colsForTab, tabKey);
    const initialFilterModel = getInitialFilter(userCustomizations);
    if (initialState && initialFilterModel) {
      if (initialState.filter) {
        initialState.filter.filterModel = initialFilterModel;
      } else {
        initialState.filter = { filterModel: initialFilterModel };
      }
    }
  }

  const handleNewRunTotalAudit = () => {
    setIsRunTotalAuditFormOpen(true);
  };

  const cardActions = () => (
    <div className="space-x-6 flex">
      <button
        className="flex items-center space-x-2"
        onClick={() => navigate('historical-charts')}
        data-cy="historical-charts-button"
        data-testid="historical-charts-button">
        <Icon type="arrowDownCircle" />
        <TextSpan
          color="indigo"
          size="sm14">
          Historical Charts
        </TextSpan>
      </button>
      <button
        className="flex items-center space-x-2"
        onClick={handleNewRunTotalAudit}
        data-cy="new-run-total-audit-button"
        data-testid="new-run-total-audit-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Audit
        </TextSpan>
      </button>
    </div>
  );

  return (
    <PageLayout
      className="w-full"
      dataTestId="run-total-audit-page"
      dataCy="run-total-audit-page-card"
      pageTitle="">
      <PageCard
        dataTestId="run-total-audit-management"
        dataCy="run-total-audit-management"
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title="Run Totals Audit"
        cardActions={cardActions()}>
        <>
          {(!initialState || runTotalAuditsLoading) && (
            <div className="flex h-[45rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          )}
          {initialState && !runTotalAuditsLoading && (
            <UpstackDataGrid
              advancedFiltering={true}
              fetchFunction={handleFetchRunTotalAudits}
              filterKeys={filterKeys}
              columns={colsForTab}
              title="Run Totals Audit"
              rows={runTotalAudits?.data || []}
              page={tabKey}
              gridSettings={gridSettings}
              showSearch={false}
              initialState={initialState}
              apiFilter={true}
              sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
              rowCount={runTotalAudits?.totalCount}
            />
          )}
          <NewRunTotalAuditDialog
            onSuccess={dataFetch}
            open={isRunTotalAuditFormOpen}
            setOpen={setIsRunTotalAuditFormOpen}
          />
        </>
      </PageCard>
    </PageLayout>
  );
};
